import React from "react";
import { Button, EditIcon } from "@pd/pd-ui-kit";
import { ColumnsType } from "antd/es/table";
import { format } from "date-fns";
import { TFunction } from "i18next";

import { ILicenseRequestRow } from "~/entity/Licenses";
import { LICENSE_ADMIN_OPTIONS } from "~/pages/LicenseManagement/adminOptions";
import { applySortToColumns } from "~/shared/ui/TableV2";

import s from "~/pages/LicenseManagement/LicenseManagement.less";

const formatShortDate = (value?: string) => {
  if (!value) {
    return "-";
  }

  try {
    return format(new Date(value), "dd.MM.yyyy");
  } catch {
    return "-";
  }
};

const COLUMN_WIDTHS: Partial<
  Record<(typeof LICENSE_ADMIN_OPTIONS)[number], number>
> = {
  year: 60,
  organization: 100,
  inn: 100,
  branchCode: 72,
  actionType: 120,
  status: 100,
  decision: 140,
  comment: 160,
  createdAt: 120,
  updatedAt: 120,
  author: 100,
  decisionUser: 162,
};

interface GetAdminLicenseActionsColumnsParams {
  t: TFunction;
  isAdmin: boolean;
  canMakeDecision: boolean;
  onMakeDecision: (record: ILicenseRequestRow) => void;
  onOpenDecisionResult: (record: ILicenseRequestRow) => void;
  onSendDraft: (record: ILicenseRequestRow) => void;
  onEditDraft: (record: ILicenseRequestRow) => void;
  sortField?: string;
  sortType?: string;
  sortableColumns?: string[];
}

export const getAdminLicenseActionsColumns = ({
  t,
  isAdmin,
  canMakeDecision,
  onMakeDecision,
  onOpenDecisionResult,
  onSendDraft,
  onEditDraft,
  sortField,
  sortType,
  sortableColumns,
}: GetAdminLicenseActionsColumnsParams): ColumnsType<ILicenseRequestRow> => {
  const editColumn = {
    title: "",
    dataIndex: "edit",
    key: "edit",
    width: 40,
    render: (_: unknown, record: ILicenseRequestRow) =>
      record.status === "draft" ? (
        <Button
          variant="text"
          color="secondary"
          size="small"
          htmlType="button"
          icon={<EditIcon size={16} />}
          aria-label={t("ui.edit")}
          title={t("ui.edit")}
          onClick={(event) => {
            event.stopPropagation();
            onEditDraft(record);
          }}
        />
      ) : null,
  };

  const columns = LICENSE_ADMIN_OPTIONS.map((option) => {
    const baseColumn = {
      title: t(`licenseManagement.table.${option}`),
      dataIndex: option,
      key: option,
      width: COLUMN_WIDTHS[option],
    };

    if (option === "decision") {
      return {
        ...baseColumn,
        render: (_: unknown, record: ILicenseRequestRow) => {
          /* PASDILER-2209: кнопка «Отправить» для черновиков временно скрыта
          if (record.status === "draft") {
            return (
              <button
                type="button"
                className={s.decisionLink}
                onClick={(event) => {
                  event.stopPropagation();
                  onSendDraft(record);
                }}
              >
                {t("ui.send")}
              </button>
            );
          }
          */

          if (isAdmin && canMakeDecision && record.needsDecision) {
            return (
              <Button
                bold
                color="secondary"
                fontSize="10"
                size="small"
                radius={30}
                onClick={(event) => {
                  event.stopPropagation();
                  onMakeDecision(record);
                }}
              >
                {t("licenseManagement.makeDecision")}
              </Button>
            );
          }

          if (record.decision) {
            return (
              <div className={s.decisionCell}>
                <span className={s.decisionStatus}>
                  {t(`licenseManagement.decisions.${record.decision}`)}
                </span>
                <button
                  type="button"
                  className={s.decisionLink}
                  onClick={(event) => {
                    event.stopPropagation();
                    onOpenDecisionResult(record);
                  }}
                >
                  {t("licenseManagement.decisionLink")}
                </button>
              </div>
            );
          }

          return <span>-</span>;
        },
      };
    }

    if (option === "status") {
      return {
        ...baseColumn,
        render: (value: string) =>
          value ? (
            <span>{t(`licenseManagement.statuses.${value}`)}</span>
          ) : (
            <span>-</span>
          ),
      };
    }

    if (option === "actionType") {
      return {
        ...baseColumn,
        render: (value: string) =>
          value ? (
            <span>{t(`licenseManagement.actionTypes.${value}`)}</span>
          ) : (
            <span>-</span>
          ),
      };
    }

    if (option === "createdAt" || option === "updatedAt") {
      return {
        ...baseColumn,
        render: (_: unknown, record: ILicenseRequestRow) => (
          <span>{formatShortDate(record[option])}</span>
        ),
      };
    }

    if (option === "year") {
      return {
        ...baseColumn,
        render: (value: number) => <span>{value || "-"}</span>,
      };
    }

    return {
      ...baseColumn,
      render: (value: string) => <span>{value || "-"}</span>,
    };
  });

  return applySortToColumns(
    [editColumn, ...columns],
    sortField,
    sortType,
    sortableColumns,
  );
};
